import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useBackspaceNavigation } from "../hooks/useBackspaceNavigation";

// Mapping for item unit codes to labels
const itemUnitLabels = {
  units: { en: 'Units', ur: 'یونٹس' },
  crates: { en: 'Crates', ur: 'کریٹس' },
  bags: { en: 'Bags', ur: 'بوریاں' },
  pali: { en: 'Pali', ur: 'پلی' },
  paiti: { en: 'Paiti', ur: 'پیتی' },
  // Case variations
  'Pali': { en: 'Pali', ur: 'پلی' },
  'PALI': { en: 'Pali', ur: 'پلی' },
  'Paiti': { en: 'Paiti', ur: 'پیتی' },
  'PAITI': { en: 'Paiti', ur: 'پیتی' },
};

function SaleView() {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Backspace navigation to dashboard
  useBackspaceNavigation();
  
  const [sale, setSale] = useState(null);
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [language, setLanguage] = useState('ur');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const saleContentRef = useRef(null);

  useEffect(() => {
    fetchSaleData();
  }, [saleId]);

  // Allow forcing language via URL (?lang=en or ?lang=ur)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const forcedLang = params.get('lang');
    if (forcedLang === 'en' || forcedLang === 'ur') {
      setLanguage(forcedLang);
    }
  }, [location.search]);

  const fetchSaleData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get(`/sale/company/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSale(response.data.sale);
      setCompany(response.data.company);
      // Default from company, but can be overridden by URL effect above
      setLanguage(response.data.company?.language || 'ur');
    } catch (error) {
      console.error("Fetch sale error:", error);
      toast.error("سیل رسید لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!saleContentRef.current) return;

    try {
      setIsGeneratingPDF(true);
      const canvas = await html2canvas(saleContentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Sale-Receipt-${sale?.serialNumber || saleId}.pdf`);
      toast.success("PDF ڈاؤن لوڈ ہو گیا!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("PDF بنانے میں ناکام");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">لوڈ ہو رہا ہے...</p>
        </div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">سیل رسید نہیں ملی</p>
          <button
            onClick={() => navigate('/company/dashboard')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            ڈیش بورڈ پر واپس جائیں
          </button>
        </div>
      </div>
    );
  }

  const balance = (sale.totalAmount || 0) - (sale.amountPaid || 0);

  return (
    <>
      <Helmet>
        <title>سیل رسید #{sale.serialNumber || saleId}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-100 py-8 px-4" dir={language === 'ur' ? 'rtl' : 'ltr'}>
        <div className="max-w-4xl mx-auto">
          {/* Action Buttons */}
          <div className="mb-6 flex gap-4 justify-end print:hidden">
            <button
              onClick={() => navigate('/company/dashboard')}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
            >
              {language === 'ur' ? 'واپس' : 'Back'}
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              {language === 'ur' ? 'پرنٹ' : 'Print'}
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50"
            >
              {isGeneratingPDF 
                ? (language === 'ur' ? 'بنا رہے ہیں...' : 'Generating...')
                : (language === 'ur' ? 'PDF ڈاؤن لوڈ' : 'Download PDF')
              }
            </button>
          </div>

          {/* Receipt Content */}
          <div
            ref={saleContentRef}
            className="bg-white shadow-lg rounded-lg p-8 print:shadow-none print:p-6"
            style={{ fontFamily: language === 'ur' ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : "system-ui, sans-serif" }}
          >
            {/* Header */}
            <div className="border-b-2 border-gray-800 pb-4 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  {company?.logo && (
                    <img
                      src={company.logo}
                      alt={company.shopName || "Company Logo"}
                      className="h-16 w-auto mb-2"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <h1 className={`text-3xl font-bold text-gray-900 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {company?.shopName || company?.username || 'Company Name'}
                  </h1>
                  {company?.address && (
                    <p className={`text-sm text-gray-600 mt-1 ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {company.address}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <h2 className={`text-2xl font-bold text-gray-900 mb-2 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'سیل رسید' : 'Sale Receipt'}
                  </h2>
                  <p className={`text-sm text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'رسید نمبر:' : 'Receipt #'}: <span className="font-semibold">{sale.serialNumber || saleId.slice(-6)}</span>
                  </p>
                  <p className={`text-sm text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'تاریخ:' : 'Date'}: {new Date(sale.date).toLocaleDateString('en-GB')}
                  </p>
                </div>
              </div>
            </div>

            {/* Merchant & Supplier Info */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Merchant Info */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h3 className={`text-lg font-semibold text-gray-900 mb-3 ${language === 'ur' ? 'font-urdu' : ''}`}>
                  {language === 'ur' ? 'گاہک کی معلومات' : 'Customer Information'}
                </h3>
                <div className="space-y-2">
                  <p className={`text-sm ${language === 'ur' ? 'font-urdu' : ''}`}>
                    <span className="font-semibold">{language === 'ur' ? 'نام:' : 'Name:'}</span>{' '}
                    {language === 'ur'
                      ? (sale.sellerNameUrdu || sale.sellerName || '-')
                      : (sale.sellerName || sale.sellerNameUrdu || '-')}
                  </p>
                  {sale.sellerNumber && (
                    <p className={`text-sm ${language === 'ur' ? 'font-urdu' : ''}`}>
                      <span className="font-semibold">{language === 'ur' ? 'فون نمبر:' : 'Phone:'}</span>{' '}
                      {sale.sellerNumber}
                    </p>
                  )}
                </div>
              </div>

              {/* Supplier Info */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h3 className={`text-lg font-semibold text-gray-900 mb-3 ${language === 'ur' ? 'font-urdu' : ''}`}>
                  {language === 'ur' ? 'فروخت کیا گیا' : 'Sold To'}
                </h3>
                <div className="space-y-2">
                  <p className={`text-sm ${language === 'ur' ? 'font-urdu' : ''}`}>
                    <span className="font-semibold">{language === 'ur' ? 'نام:' : 'Name:'}</span>{' '}
                    {language === 'ur'
                      ? (sale.supplierNameUrdu || sale.supplierName || '-')
                      : (sale.supplierName || sale.supplierNameUrdu || '-')}
                  </p>
                  {sale.supplierVehicleNumber && (
                    <p className={`text-sm ${language === 'ur' ? 'font-urdu' : ''}`}>
                      <span className="font-semibold">{language === 'ur' ? 'گاڑی نمبر:' : 'Vehicle #:'}</span>{' '}
                      {sale.supplierVehicleNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-6">
              <h3 className={`text-lg font-semibold text-gray-900 mb-3 ${language === 'ur' ? 'font-urdu' : ''}`}>
                {language === 'ur' ? 'خریداری کی تفصیل' : 'Items Details'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className={`border border-gray-300 px-4 py-2 text-right ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'تفصیل' : 'Description'}
                      </th>
                      <th className={`border border-gray-300 px-4 py-2 text-center ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'یونٹ' : 'Unit'}
                      </th>
                      <th className={`border border-gray-300 px-4 py-2 text-center ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'دانہ' : 'Dana'}
                      </th>
                      <th className={`border border-gray-300 px-4 py-2 text-center ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'مقدار' : 'Quantity'}
                      </th>
                      <th className={`border border-gray-300 px-4 py-2 text-center ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'فی یونٹ قیمت' : 'Rate/Unit'}
                      </th>
                      <th className={`border border-gray-300 px-4 py-2 text-center ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'کل رقم' : 'Total'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sale.items && sale.items.length > 0 ? (
                      sale.items.map((item, index) => (
                        <tr key={index}>
                          <td className={`border border-gray-300 px-4 py-2 ${language === 'ur' ? 'font-urdu' : ''}`}>
                            {language === 'ur'
                              ? (item.descriptionUrdu || item.description || '-')
                              : (item.description || item.descriptionUrdu || '-')}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {(() => {
                              const unitKey = (item.unit || '').toLowerCase();
                              return itemUnitLabels[unitKey]?.[language === 'ur' ? 'ur' : 'en'] || itemUnitLabels[item.unit]?.[language === 'ur' ? 'ur' : 'en'] || item.unit || '-';
                            })()}
                          </td>
                          <td className={`border border-gray-300 px-4 py-2 text-center ${language === 'ur' ? 'font-urdu' : ''}`}>
                            {item.dana || '-'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {item.quantity || 0}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {(item.ratePerUnit || 0).toLocaleString()}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center font-semibold">
                            {(item.lineTotal || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className={`border border-gray-300 px-4 py-4 text-center text-gray-500 ${language === 'ur' ? 'font-urdu' : ''}`}>
                          {language === 'ur' ? 'کوئی آئٹم نہیں' : 'No items'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary */}
            <div className="border-t-2 border-gray-800 pt-4">
              <div className="flex justify-end">
                <div className="w-full md:w-1/2 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {language === 'ur' ? 'ذیلی کل:' : 'Subtotal:'}
                    </span>
                    <span className="font-semibold">{(sale.subtotal || 0).toLocaleString()}</span>
                  </div>
                  {sale.discount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className={`text-sm font-medium ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'رعایت:' : 'Discount:'}
                      </span>
                      <span className="font-semibold text-red-600">-{(sale.discount || 0).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t-2 border-gray-400 pt-2">
                    <span className={`text-lg font-bold ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {language === 'ur' ? 'کل رقم:' : 'Total Amount:'}
                    </span>
                    <span className="text-lg font-bold">{(sale.totalAmount || 0).toLocaleString()}</span>
                  </div>
                  {sale.paymentMethod && (
                    <div className="flex justify-between items-center mt-2">
                      <span className={`text-sm font-medium ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'ادائیگی کا طریقہ:' : 'Payment Method:'}
                      </span>
                      <span className="font-semibold">{sale.paymentMethod}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {language === 'ur' ? 'ادا کی گئی رقم:' : 'Amount Paid:'}
                    </span>
                    <span className="font-semibold text-green-600">{(sale.amountPaid || 0).toLocaleString()}</span>
                  </div>
                  {balance > 0 && (
                    <div className="flex justify-between items-center border-t-2 border-gray-400 pt-2">
                      <span className={`text-sm font-medium text-red-600 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'بقایا:' : 'Balance:'}
                      </span>
                      <span className="font-semibold text-red-600">{balance.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t border-gray-300 text-center">
              <p className={`text-xs text-gray-500 ${language === 'ur' ? 'font-urdu' : ''}`}>
                {language === 'ur' 
                  ? 'یہ رسید کمپیوٹر سے تیار کی گئی ہے اور دستخط کی ضرورت نہیں'
                  : 'This receipt is computer generated and does not require a signature'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:shadow-none,
          .print\\:shadow-none * {
            visibility: visible;
          }
          .print\\:shadow-none {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}

export default SaleView;

